import * as THREE from "three";

import { XR8Module } from "@/typings/XR8";
import * as TWEEN from "@tweenjs/tween.js";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import getSceneManager from "@/argo_ar/SceneManager";
import { Object3D } from "three";
import { getPit } from "@/argo_ar/ArgoUtils";
import getQrCodeService from "@/services/QrCodeService";
import getMarkerService from "@/services/MarkerService";
import { isIOS } from "@/utils";
import store from "@/store/";

import lightConfigs from "./lighting.config";
import { LIGHT_TYPES } from "../typings/Lights";

declare const XR8: XR8Module;

export enum EVENTS {
    ASSET_DISPLAYED = "assetDisplayed",
    ASSET_HIDDEN = "assetHidden",
    ASSET_LOAD_START = "assetLoadStart",
    ASSET_LOAD_FINISHED = "assetLoadFinished",
    ASSET_PLAY_START = "assetPlayStart",
    ASSET_PLAY_STOP = "assetPlayStop",
    VIDEO_PLAY_START = "videoPlayStart",
    VIDEO_PLAY_STOP = "videoPlayStop"
}

enum PLAYING_STATUS {
    CURSOR_DISPLAYED = 0,
    ASSET_DISPLAYED = 1,
    VIDEO_PLAYING = 2
}

// Pipeline module that initializes the threejs scene when the camera feed starts
class PlacegroundPipeline {
    // Pipeline modules need a name. It can be whatever you want but must be unique within your app.
    private name = "placeground";
    private assetUrl: string; // 3D model to spawn at tap
    private assetModel = null as Object3D | null;
    private assetModelMixer: THREE.AnimationMixer;
    private assetModelActions: Array<THREE.AnimationAction> = new Array<THREE.AnimationAction>();
    private videoAssetHTML: HTMLVideoElement;
    private videoAssetHTMLName = "replace_1_hide";
    private assetVideoPlane: THREE.Mesh;
    private assetVideoPlaneName = "proj_video_plane";
    private assetPlayButtonName = "play_button";
    private assetPlayButton: THREE.Mesh;
    private assetPlayButtonParent: THREE.Object3D;

    private startScale = 0.0001; // Initial scale value for our model;
    private endScale = 9; // Ending scale value for our model
    private animationMillis = 750; // Animate over 0.75 seconds;

    // 2D coordinates of the raycast origin, in normalized device coordinates (NDC)---X and Y
    // components should be between -1 and 1.  Here we want the cursor in the center of the screen.
    private renderer: THREE.WebGLRenderer;
    private rayOrigin: THREE.Vector2 = new THREE.Vector2(0, 0);
    private mousePos: THREE.Vector2 = new THREE.Vector2(0, 0);
    private raycaster: THREE.Raycaster = new THREE.Raycaster();
    private tapPosition: THREE.Vector2 = new THREE.Vector2();

    private scene: THREE.Scene;
    private camera: THREE.Camera;

    private loader: GLTFLoader = new GLTFLoader();
    private surface = null as THREE.Mesh | null; // Transparent surface for raycasting for object placement.

    private cursorUrl = "/clickme.glb";
    private cursorScale = new THREE.Vector3(0.01, 0.01, 0.01);
    private cursorMixer: THREE.AnimationMixer;
    private cursorLocation: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
    private cursorObject: GLTF | null = null;
    private isCursorDisplayed = false;
    private clock: THREE.Clock = new THREE.Clock();
    private textureCube: THREE.CubeTexture;
    private ambientLight: THREE.AmbientLight;
    private directionalLight: THREE.DirectionalLight;
    private playingStatus = PLAYING_STATUS.CURSOR_DISPLAYED; //0 cursor //1 start animation //2 anim with Video
    private canvas = null as HTMLElement | null;

    private pageId: number;
    private deviceId: string;

    private isIOS: boolean;
    public isLoadingAsset: boolean;

    private eventListeners: { [key in EVENTS]?: Function };

    constructor(
        pageId: number,
        deviceId: string,
        eventListeners: { [key in EVENTS]?: Function },
        modelScale = 9
    ) {
        this.pageId = pageId;
        this.deviceId = deviceId;
        this.eventListeners = eventListeners;
        this.isLoadingAsset = false;
        this.setEnvMap();
        this.loadCursorModel();
        this.loadAssets();
        this.isIOS = isIOS();
        this.endScale = modelScale;
    }

    /**
     * Called once the pipeline is started.
     * Draw the ground surface & start displaying the cursor on it
     * @param scene 3D scene
     * @param camera 3D camera of the scene
     */
    private initXrScene = (scene: THREE.Scene, camera: THREE.Camera) => {
        console.log("PP.initXrScene");
        this.surface = new THREE.Mesh(
            new THREE.PlaneGeometry(100, 100, 1, 1),
            new THREE.MeshBasicMaterial({
                color: 0xffff00,
                transparent: true,
                opacity: 0.0,
                side: THREE.DoubleSide
            })
        );

        this.surface.rotateX(-Math.PI / 2);
        this.surface.position.set(0, 0, 0);
        scene.add(this.surface);

        // Initialize lights based on the configuration file
        lightConfigs.forEach(lightConfig => {
            let light: THREE.Light;
            switch (lightConfig.type) {
                case LIGHT_TYPES.AMBIENT:
                    light = new THREE.AmbientLight(lightConfig.color, lightConfig.intensity);
                    break;

                case LIGHT_TYPES.DIRECTIONAL:
                    light = new THREE.DirectionalLight(lightConfig.color, lightConfig.intensity);
                    break;
            }

            if (lightConfig.position) {
                light.position.set(
                    lightConfig.position.x,
                    lightConfig.position.y,
                    lightConfig.position.z
                );
            }

            camera.add(light);
        });

        scene.environment = this.textureCube;

        // Set the initial camera position relative to the scene we just laid out. This must be at a
        // height greater than y=0.
        camera.position.set(0, 3, 0);

        // Add the cursor to the scene
        if (this.cursorObject) {
            this.showCursor();
        }
    };

    private setEnvMap() {
        const loader = new THREE.CubeTextureLoader();
        loader.setPath("cubemap/");

        const envMap = loader.load([
            "office-xpos.png",
            "office-xneg.png",
            "office-ypos.png",
            "office-yneg.png",
            "office-zpos.png",
            "office-zpos.png"
        ]);

        this.textureCube = envMap;
    }

    /**
     * Load the 3D model of the cursor
     */
    private loadCursorModel() {
        this.loader.load(
            this.cursorUrl, // resource URL.
            cursorObject => {
                this.cursorObject = cursorObject;
                this.cursorMixer = new THREE.AnimationMixer(this.cursorObject.scene);
                this.cursorObject.animations.forEach(animation => {
                    const action = this.cursorMixer.clipAction(animation);
                    action.play();
                });
                this.cursorObject.scene.scale.set(
                    this.cursorScale.x,
                    this.cursorScale.y,
                    this.cursorScale.z
                );
                this.cursorObject.scene.position.set(
                    this.cursorLocation.x,
                    0.0,
                    this.cursorLocation.z
                );

                // Add the cursor to the scene
                this.showCursor();
            }, // loaded handler.
            xhr => {
                console.log(`${(xhr.loaded / xhr.total) * 100}% cursor loaded`);
            }, // progress handler.
            error => {
                console.log("An error happened");
                console.log(error);
            } // error handler.
        );
    }

    /**
     * Pop a 3D object on the scene with an animation
     * @param model Model to pop
     * @param pointX X position on the scene
     * @param pointZ Z position on the scene
     * @param yDegrees Y rotation in degrees
     */
    private animateIn = (
        model: THREE.Object3D,
        pointX: number,
        pointZ: number,
        yDegrees: number,
        startScaleValue: number,
        endScaleValue: number
    ) => {
        const startScale = new THREE.Vector3(startScaleValue, startScaleValue, startScaleValue);
        const endScale = new THREE.Vector3(endScaleValue, endScaleValue, endScaleValue);
        const scale = Object.assign({}, startScale);

        model.position.set(pointX, 0.0, pointZ);

        const targetPosition = this.camera.position.clone();
        targetPosition.y = 0;
        const rotationLoad=new THREE.Vector3(); 
        rotationLoad.set(model.rotation.x,model.rotation.y,model.rotation.z)
        model.rotation.set(-Math.PI/2.,model.rotation.y,rotationLoad.z)

        model.scale.set(scale.x, scale.y, scale.z);
        this.scene.add(model);

        new TWEEN.Tween(scale)
            .to(endScale, this.animationMillis)
            .easing(TWEEN.Easing.Elastic.Out) // Use an easing function to make the animation smooth.
            .onUpdate(() => {
                model.scale.set(scale.x, scale.y, scale.z);
            })
            .start(); // Start the tween immediately.
    };

    /**
     * Update the cursor position based on where the camera looks
     */
    private updateCursorLocation() {
        // Raycast from camera to 'ground'
        // Update the picking ray with the camera and tap position.
        this.raycaster.setFromCamera(this.rayOrigin, this.camera);

        // Raycast against the "surface" object.
        const intersects = this.raycaster.intersectObject(this.surface as THREE.Mesh, true);

        if (!intersects.length) {
            return;
        }

        const [intersect] = intersects;
        this.cursorLocation = this.cursorLocation.lerp(intersect.point, 0.2);

        // update cursor model position
        if (this.cursorObject) {
            this.cursorObject.scene.position.set(this.cursorLocation.x, 0, this.cursorLocation.z);
        }
    }

    private getVideoShader(isRotated = false) {
        //console.log("getVideoShader "+isRotated);
        const videoTexture = new THREE.VideoTexture(this.videoAssetHTML);
        if (!isRotated) {
            const inversedMat = new THREE.ShaderMaterial({
                transparent: true,
                fragmentShader: `
                    uniform sampler2D videoTexture;
                    varying vec2 vUv;
                    void main(void) {
                        gl_FragColor = texture2D( videoTexture, vec2(vUv.x, 1. -vUv.y) );
                    }
                    `,
                vertexShader: `
                    varying vec2 vUv;
                    void main() {
                        vUv = uv;
                        gl_PointSize = 8.0;
                        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                    }
                    `,
                uniforms: {
                    videoTexture: { value: videoTexture }
                }
            });
            return inversedMat;
        } else {
            const inversedMat = new THREE.ShaderMaterial({
                transparent: true,
                fragmentShader: `
                    uniform sampler2D videoTexture;
                    varying vec2 vUv;
                    void main(void) {
                        gl_FragColor = texture2D( videoTexture, vec2(vUv.x, vUv.y) );
                    }
                    `,
                vertexShader: `
                    varying vec2 vUv;
                    void main() {
                        vUv = uv;
                        gl_PointSize = 8.0;
                        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                    }
                    `,
                uniforms: {
                    videoTexture: { value: videoTexture }
                }
            });
            return inversedMat;
        }
    }
    /**
     * Load the 3D models of the assets
     */
    private async loadAssets() {
        console.warn("loadAssets");
        this.runListener(EVENTS.ASSET_LOAD_START);
        this.isLoadingAsset = true;
        const sceneManager = getSceneManager();

        // clear pit
        while (getPit().childNodes.length > 0) {
            getPit().removeChild(getPit().childNodes[0]);
        }

        // load marker
        const marker = await getMarkerService().loadMarker(this.pageId, this.deviceId);

        /**
         * init sceneManager if not already
         * clear scene if already initialized
         */
        if (!sceneManager.initialized) {
            sceneManager.init(this.pageId, this.deviceId);
        } else {
            sceneManager.resetScene();
        }

        // load assets in scene
        await sceneManager.loadMarkerAssets(marker);

        this.assetModel = sceneManager.scene;
        /* eslint-disable @typescript-eslint/ban-ts-ignore */
        //@ts-ignore
        window.assetModel = this.assetModel;
        /* eslint-disable @typescript-eslint/ban-ts-ignore */
        //@ts-ignore
        window.camera = this.camera;

        this.assetModel.traverse(child => {
            if (child instanceof THREE.Mesh) {
                const childName = child.name;
                if (childName === this.assetVideoPlaneName) {
                    this.assetVideoPlane = child;
                } else if (childName === this.videoAssetHTMLName) {
                    //console.log("found video...");
                } else if (childName === this.assetPlayButtonName) {
                    this.assetPlayButton = child;
                    this.assetPlayButtonParent = child.parent;
                }
            }

            if (child.mixer) {
                this.assetModelMixer = child.mixer;
                const animations = child.animations;

                this.assetModelActions = animations.map((clip, index) => {
                    const action = this.assetModelMixer.clipAction(clip);
                    // we want the first action to repear once
                    // and the others to repeat infinite
                    action.setLoop(index === 0 ? THREE.LoopOnce : THREE.LoopRepeat, Infinity);
                    return action;
                });
            }
        });

        this.configureVideoAsset();

        this.isLoadingAsset = false;
        this.runListener(EVENTS.ASSET_LOAD_FINISHED);

        return sceneManager.scene;
    }

    private configureVideoAsset = () => {
        this.videoAssetHTML = document.createElement("video");
        this.videoAssetHTML.setAttribute("crossorigin", "anonymous");
        this.videoAssetHTML.setAttribute("webkit-playsinline", "");
        this.videoAssetHTML.setAttribute("playsinline", "");
        this.videoAssetHTML.style.display = "none";
        const id = "mainvideo";
        this.videoAssetHTML.setAttribute("id", id);
        getPit().appendChild(this.videoAssetHTML);

        if (!this.videoAssetHTML) {
            throw new Error("Could not find HTML video element");
        }

        this.videoAssetHTML.addEventListener("play", () => {
            if (this.assetPlayButton && this.assetPlayButtonParent) {
                this.assetPlayButtonParent.remove(this.assetPlayButton);
            }
        });

        this.videoAssetHTML.addEventListener("pause", () => {
            if (this.assetPlayButton && this.assetPlayButtonParent) {
                this.assetPlayButtonParent.add(this.assetPlayButton);
            }
        });

        this.videoAssetHTML.addEventListener("ended", () => {
            if (this.assetPlayButton && this.assetPlayButtonParent) {
                this.assetPlayButtonParent.add(this.assetPlayButton);
            }
        });

        // when video ready to play, set the shader of the video plane to display first image of the video
        this.videoAssetHTML.oncanplay = () => {
            if (this.assetVideoPlane) {
                this.assetVideoPlane.material = this.getVideoShader(
                    this.assetVideoPlane.geometry.type == "PlaneGeometry"
                );
            }
        };

        // t=0.1 to get first image placeholder on iOS
        const videoUrl = store.state.qrcode?.videoUrl;
        if (videoUrl.startsWith("http")) {
            this.videoAssetHTML.src = videoUrl + "#t=0.1";
        } else {
            const urlEnd = videoUrl + "#t=0.1";
            this.videoAssetHTML.src = getQrCodeService().getFullVideoPath(urlEnd);
        }
    };

    //Call every time isPlaying should change
    private changePlayingStatus(newPlayingStatus: PLAYING_STATUS) {
        if (this.playingStatus === newPlayingStatus) {
            return;
        }
        this.playingStatus = newPlayingStatus;
        switch (this.playingStatus) {
            case PLAYING_STATUS.CURSOR_DISPLAYED:
                this.videoAssetHTML.currentTime = 0.1;
                this.renderer.physicallyCorrectLights = false;
                this.renderer.toneMappingExposure = 1;
                this.renderer.toneMapping = 1;
                if (this.assetModelActions.length > 1) {
                    this.assetModelActions[0].stop();
                    this.assetModelActions[1].stop();
                }
                this.runListener(EVENTS.ASSET_PLAY_STOP);
                break;

            case PLAYING_STATUS.ASSET_DISPLAYED:
                this.runListener(EVENTS.ASSET_PLAY_START);

                this.clock = new THREE.Clock(); //restart the clock for anim
                if (this.assetModelActions.length > 0) {
                    this.assetModelActions[0].play();
                }

                this.animateAsset();
                break;

            case PLAYING_STATUS.VIDEO_PLAYING:
                if (this.assetModelActions.length > 1) {
                    this.assetModelActions[0].stop();
                    this.assetModelActions[1].play();
                }

                this.videoAssetHTML.play();
                this.runListener(EVENTS.VIDEO_PLAY_START);
                break;
        }
    }

    private runListener(event: string) {
        console.log("run listener", event);
        if (this.eventListeners[event]) {
            this.eventListeners[event]();
        }
    }

    private onPlayStart() {
        //once start anim done, start loop
        this.changePlayingStatus(PLAYING_STATUS.ASSET_DISPLAYED);

        if (typeof this.assetModelMixer !== "undefined") {
            this.assetModelMixer.addEventListener("finished", () => {
                if (this.playingStatus === PLAYING_STATUS.ASSET_DISPLAYED) {
                    if (this.assetModelActions.length > 1) {
                        this.assetModelActions[0].stop();
                        this.assetModelActions[1].play();
                    }
                    if (!this.isIOS) {
                        this.changePlayingStatus(PLAYING_STATUS.VIDEO_PLAYING);
                    }
                }
            });
        }
    }

    public stopPlayingAsset = () => {
        if (this.assetModel) {
            try {
                this.videoAssetHTML.pause();
            } catch (e) {
                console.error(e);
            }
            this.scene.remove(this.assetModel);

            this.runListener(EVENTS.ASSET_HIDDEN);
            this.changePlayingStatus(PLAYING_STATUS.CURSOR_DISPLAYED);
        }
    };

    // Load the glb model at the requested point on the surface.
    public playAsset = async (pointX: number, pointZ: number) => {
        // already playing, do nothing
        if (this.playingStatus !== PLAYING_STATUS.CURSOR_DISPLAYED) {
            return;
        }

        this.runListener(EVENTS.ASSET_DISPLAYED);

        try {
            if (!this.assetModel) {
                await this.loadAssets();
            }
        } catch (e) {
            console.error(e);
        }
        this.isLoadingAsset = false;

        if (this.assetModel) {
            this.animateIn(this.assetModel, pointX, pointZ, 0, this.startScale, this.endScale);
            this.onPlayStart();

            this.object3DLookatCamera(this.assetModel);
            this.assetModel.rotateX(-Math.PI/2.)

            // need to play the video for a little time to display first video frame on the plane
            this.videoAssetHTML.setAttribute("muted", "muted");
            this.videoAssetHTML.muted = true;
            this.videoAssetHTML.play();
            setTimeout(() => {
                this.videoAssetHTML.pause();
                this.videoAssetHTML.currentTime = 0.1;

                this.videoAssetHTML.removeAttribute("muted");
                this.videoAssetHTML.muted = false;
            }, 10);
        }
    };

    /**
     * Add the assets to the scene to make them appear on the given screen position
     * @param left Left position of the asset on the screen
     * @param top Top position of the asset on the screen
     */
    public playAssetOnScreen = (left: number, top: number) => {
        const position = new THREE.Vector2(left, top);

        // Update the picking ray with the camera and tap position.
        this.raycaster.setFromCamera(position, this.camera);

        // Raycast against the "surface" object.
        const intersects = this.raycaster.intersectObject(this.surface as THREE.Mesh);

        if (intersects.length === 1 && intersects[0].object === this.surface) {
            this.playAsset(intersects[0].point.x, intersects[0].point.z);
        }
    };

    private updateMouseCoordinates(event) {
        const positionRect = this.renderer.domElement.getBoundingClientRect();
        const positionTop = positionRect.top + document.body.scrollTop;
        const positionLeft = positionRect.left + document.body.scrollLeft;

        if (event.clientX != undefined) {
            this.mousePos.x =
                ((event.clientX - positionLeft) / this.renderer.domElement.clientWidth) * 2 - 1;
            this.mousePos.y =
                -((event.clientY - positionTop) / this.renderer.domElement.clientHeight) * 2 + 1;
        } else {
            this.mousePos.x =
                ((event.touches[0].pageX - positionLeft) / this.renderer.domElement.clientWidth) *
                    2 -
                1;
            this.mousePos.y =
                -((event.touches[0].pageY + positionTop) / this.renderer.domElement.clientHeight) *
                    2 +
                1;
        }
    }

    private object3DLookatCamera(obj:THREE.Object3D):void{
        const targetPosition = this.camera.position.clone();
        targetPosition.y = 0;
        //let newRot=new THREE.Euler(this.marker.rotation.x,this.camera.rotation.y,this.marker.rotation.z,"YXZ")
        obj.lookAt(targetPosition)
    }

    private checkEditorSceneClick(intersects: Array<THREE.Intersection>) {
        if (intersects.length == 0) {
            return;
        }
        const asset = intersects[0].object as {
            onClick?: Function;
            parent?: { onClick?: Function };
        };
        if (asset.onClick) {
            asset.onClick(asset);
        }
    }

    private checkCustom3DClick(intersects: Array<THREE.Intersection>) {
        if (intersects.length == 0) {
            return;
        }
        let intersected = "no";
        for (let i = 0; i < 1; i++) {
            if (intersects[i].object.name.includes("@@")) {
                intersected = intersects[i].object.name;
            }
        }
        const clickableSplit = intersected.split("@@");
        if (clickableSplit.length == 2) {
            const action = clickableSplit[0].trim();
            const data = atob(clickableSplit[1]);
            switch (action) {
                case "url":
                    window.open(data, "_blank");
                    break;
                case "phone":
                    window.open("tel:" + data, "_blank");
                    break;
                case "mailto":
                    window.open("mailto:" + data, "_blank");
                    break;
                default:
                    break;
            }
        }
    }

    private onClick(e) {
        this.updateMouseCoordinates(e);
        const meshes = new Array<THREE.Mesh>();

        this.assetModel.traverse(function(c) {
            if (c instanceof THREE.Mesh) {
                meshes.push(c as THREE.Mesh);
            }
        });
        this.raycaster.setFromCamera(this.mousePos, this.camera);
        const intersects = this.raycaster.intersectObjects(meshes);
        this.checkEditorSceneClick(intersects);
        this.checkCustom3DClick(intersects);

        if (this.playingStatus === PLAYING_STATUS.CURSOR_DISPLAYED) {
            this.hideCursor();
            this.playAsset(this.cursorLocation.x, this.cursorLocation.z);
        } else {
            // if click on the 3D object
            if (intersects.length > 0) {
                //check if clicked on videoPlane
                if (this.playingStatus !== PLAYING_STATUS.VIDEO_PLAYING) {
                    this.changePlayingStatus(PLAYING_STATUS.VIDEO_PLAYING);
                } else {
                    //check if clicked on videoplane
                    let isVideoPlane = false;
                    for (let i = 0; i < 1; i++) {
                        //intersects.length
                        if (
                            intersects[i].object.name === this.assetVideoPlaneName ||
                            intersects[i].object.name === this.assetPlayButtonName
                        ) {
                            isVideoPlane = true;
                        }
                    }
                    if (isVideoPlane) {
                        if (this.videoAssetHTML.paused || this.videoAssetHTML.ended) {
                            this.videoAssetHTML.play();
                        } else {
                            this.videoAssetHTML.pause();
                        }
                    }
                }
            } else {
                //replace 3D
                const intersect = this.raycaster.intersectObject(this.surface as THREE.Mesh, true);

                if (intersect.length === 1 && intersect[0].object === this.surface) {
                    this.assetModel.position.set(intersect[0].point.x, 0.0, intersect[0].point.z);

                    const targetPosition = this.camera.position.clone();
                    targetPosition.y = 0;
                    this.assetModel.lookAt(targetPosition);
                }
            }
        }
    }

    public startWithGyro(
        canvas: HTMLCanvasElement,
        scene: THREE.Scene,
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer
    ) {
        this.canvas = canvas;
        this.scene = scene;
        this.camera = camera;
        this.renderer = renderer;

        this.start();
    }

    // onStart is called once when the camera feed begins. In this case, we need to wait for the
    // XR8.Threejs scene to be ready before we can access it to add content. It was created in
    // XR8.Threejs.pipelineModule()'s onStart method.
    public onStart = ({ canvas }: { canvas: HTMLElement }) => {
        const { scene, camera, renderer } = XR8.Threejs.xrScene(); // Get the 3js sceen from xr3js.

        this.scene = scene;
        this.camera = camera;
        this.renderer = renderer as THREE.WebGLRenderer;
        this.canvas = canvas;

        this.start();

        // Sync the xr controller's 6DoF position and camera paremeters with our scene.
        XR8.XrController.updateCameraProjectionMatrix({
            origin: camera.position,
            facing: camera.quaternion
        });
    };

    private start() {
        this.initXrScene(this.scene, this.camera); // Add objects to the scene and set starting camera position.
        this.canvas.addEventListener("click", this.onClick.bind(this)); // Add touch listener.
        // Start animations (TWEEN & cursor).
        this.animateCursor();
    }

    private onDetach = () => {
        if (this.videoAssetHTML) {
            this.videoAssetHTML.pause();
        }
    };

    /**
     * Called everytime possible using requestAnimationFrame.
     * Update Tween animation & cursor position.
     */
    private animateAsset = (time?: number) => {
        if (
            this.assetModel.parent === this.surface ||
            this.playingStatus === PLAYING_STATUS.CURSOR_DISPLAYED
        ) {
            return;
        }

        TWEEN.update(time);
        if (this.assetModelMixer != undefined) {
            this.assetModelMixer.update(this.clock.getDelta());
        }
        requestAnimationFrame(this.animateAsset);
    };

    /**
     * Called everytime possible using requestAnimationFrame.
     * Update Tween animation & cursor position.
     */
    private animateCursor = (time?: number) => {
        if (!this.isCursorDisplayed) {
            return;
        }
        requestAnimationFrame(this.animateCursor);
        this.cursorMixer.update(this.clock.getDelta());
        TWEEN.update(time);
        this.updateCursorLocation();
    };

    public hideCursor = () => {
        if (this.isCursorDisplayed && this.cursorObject) {
            this.scene.remove(this.cursorObject.scene);
            this.isCursorDisplayed = false;
        }
    };

    public showCursor = () => {
        if (this.cursorObject && !this.isCursorDisplayed && this.scene) {
            this.scene.add(this.cursorObject.scene);
            this.cursorObject.scene.name = "cursor";
            this.isCursorDisplayed = true;
            this.animateCursor();
        }
    };
}

export default PlacegroundPipeline;
